import { useMemo, useState } from "react";
import {
  CheckCircle2,
  CircleAlert,
  Edit3,
  Link2,
  Plus,
  ShieldAlert,
  Trash2,
  X,
} from "lucide-react";
import { EmptyState, Field, StatusPill } from "../components/Ui";
import { riskCompletionIssues } from "../lib/assessmentEngine";
import { blankRisk } from "../lib/projectStore";
import type {
  CriticalControl,
  DesignRisk,
  DocumentRevision,
  EvidenceItem,
  IncidentRecord,
  LifecycleStage,
  ReviewStatus,
  RiskLevel,
} from "../types/domain";

interface RisksProps {
  risks: DesignRisk[];
  evidence: EvidenceItem[];
  controls: CriticalControl[];
  revisions: DocumentRevision[];
  incidents: IncidentRecord[];
  onSave: (risk: DesignRisk) => void;
  onRemove: (riskId: string) => void;
}

export function RisksView({
  risks,
  evidence,
  controls,
  revisions,
  incidents,
  onSave,
  onRemove,
}: RisksProps) {
  const [draft, setDraft] = useState<DesignRisk | null>(null);
  const [filter, setFilter] = useState<"all" | ReviewStatus>("all");

  const visibleRisks = useMemo(
    () => risks.filter((risk) => filter === "all" || risk.reviewStatus === filter),
    [risks, filter],
  );

  const saveDraft = () => {
    if (!draft) return;
    onSave(draft);
    setDraft(null);
  };

  return (
    <>
      <section className="toolbar">
        <div className="segmented-control" aria-label="Filter design risks">
          {(["all", "draft", "reviewed", "approved"] as const).map((value) => (
            <button
              key={value}
              className={filter === value ? "active" : ""}
              onClick={() => setFilter(value)}
            >
              {value}
              <span>
                {value === "all"
                  ? risks.length
                  : risks.filter((risk) => risk.reviewStatus === value).length}
              </span>
            </button>
          ))}
        </div>
        <button className="button button-primary" onClick={() => setDraft(blankRisk())}>
          <Plus size={18} />
          Add design risk
        </button>
      </section>

      {visibleRisks.length === 0 ? (
        <EmptyState
          icon={<ShieldAlert size={30} />}
          title={risks.length ? "No risks match this filter" : "No design risks recorded"}
          action={
            !risks.length && (
              <button className="button button-primary" onClick={() => setDraft(blankRisk())}>
                <Plus size={18} /> Record the first risk
              </button>
            )
          }
        >
          Record the design element, safer options considered, the selected decision, residual risk
          and its controlled design reference.
        </EmptyState>
      ) : (
        <section className="risk-list">
          {visibleRisks.map((risk) => {
            const issues = riskCompletionIssues(risk);
            const linkedEvidence = evidence.filter((item) => risk.evidenceIds.includes(item.id));
            return (
              <article className="risk-card" key={risk.id}>
                <div className="risk-severity" data-risk={risk.residualRisk} />
                <div className="risk-card-body">
                  <div className="risk-title-row">
                    <div>
                      <span className="stage-tag">{risk.lifecycleStage.replace("-", " ")}</span>
                      <h3>{risk.designElement || "Untitled design element"}</h3>
                    </div>
                    <div className="risk-actions">
                      <StatusPill
                        status={
                          risk.reviewStatus === "approved"
                            ? "success"
                            : risk.reviewStatus === "reviewed"
                              ? "warning"
                              : "neutral"
                        }
                      >
                        {risk.reviewStatus}
                      </StatusPill>
                      <button
                        className="icon-button"
                        aria-label="Edit risk"
                        onClick={() => setDraft(risk)}
                      >
                        <Edit3 size={17} />
                      </button>
                      <button
                        className="icon-button danger"
                        aria-label="Delete risk"
                        onClick={() => {
                          if (window.confirm("Delete this design-risk record?")) onRemove(risk.id);
                        }}
                      >
                        <Trash2 size={17} />
                      </button>
                    </div>
                  </div>
                  <p className="risk-hazard">{risk.hazard || "No hazard statement recorded."}</p>
                  <div className="risk-decision-grid">
                    <div>
                      <small>Elimination considered</small>
                      <p>{risk.eliminationConsidered || "Not recorded"}</p>
                    </div>
                    <div>
                      <small>Design decision</small>
                      <p>{risk.designDecision || "Not recorded"}</p>
                    </div>
                    <div>
                      <small>Residual control</small>
                      <p>{risk.control || "Not recorded"}</p>
                    </div>
                  </div>
                  <div className="risk-footer">
                    <span>
                      <strong>Reference</strong> {risk.drawingReference || "Missing"}
                    </span>
                    <span>
                      <strong>Owner</strong> {risk.owner || "Unassigned"}
                    </span>
                    <span>
                      <Link2 size={14} />
                      {linkedEvidence.length} evidence item{linkedEvidence.length === 1 ? "" : "s"}
                    </span>
                    <span>{risk.controlIds.length} critical control(s)</span>
                    <span>{risk.revisionIds.length} controlled revision(s)</span>
                  </div>
                  {issues.length > 0 && (
                    <div className="quality-gaps">
                      <CircleAlert size={17} />
                      <span>
                        <strong>{issues.length} quality gap{issues.length === 1 ? "" : "s"}</strong>
                        {issues[0]}
                      </span>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </section>
      )}

      {draft && (
        <div className="modal-backdrop" role="presentation">
          <section className="modal-card" role="dialog" aria-modal="true">
            <div className="modal-header">
              <div>
                <span className="eyebrow">Controlled design decision</span>
                <h2>{risks.some((risk) => risk.id === draft.id) ? "Edit design risk" : "Add design risk"}</h2>
              </div>
              <button className="icon-button" onClick={() => setDraft(null)} aria-label="Close">
                <X size={21} />
              </button>
            </div>
            <div className="modal-scroll">
              <div className="form-grid">
                <Field label="Design element" wide>
                  <input
                    value={draft.designElement}
                    onChange={(event) => setDraft({ ...draft, designElement: event.target.value })}
                    placeholder="e.g. Rooftop plant access"
                  />
                </Field>
                <Field label="Lifecycle stage">
                  <select
                    value={draft.lifecycleStage}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        lifecycleStage: event.target.value as LifecycleStage,
                      })
                    }
                  >
                    <option value="pre-construction">Pre-construction</option>
                    <option value="construction">Construction</option>
                    <option value="use">Use</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="demolition">Demolition</option>
                  </select>
                </Field>
                <Field label="Residual risk">
                  <select
                    value={draft.residualRisk}
                    onChange={(event) =>
                      setDraft({ ...draft, residualRisk: event.target.value as RiskLevel })
                    }
                  >
                    <option value="not-assessed">Not assessed</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </Field>
                <Field label="Risk category">
                  <input
                    value={draft.category}
                    onChange={(event) => setDraft({ ...draft, category: event.target.value })}
                    placeholder="e.g. Work at height"
                  />
                </Field>
                <Field label="Origin">
                  <select
                    value={draft.origin}
                    onChange={(event) =>
                      setDraft({ ...draft, origin: event.target.value as DesignRisk["origin"] })
                    }
                  >
                    <option value="design">Design review</option>
                    <option value="construction">Construction planning</option>
                    <option value="incident">Incident learning</option>
                    <option value="regulatory">Regulatory assessment</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
                <Field label="Hazard and credible harm" wide>
                  <textarea
                    rows={3}
                    value={draft.hazard}
                    onChange={(event) => setDraft({ ...draft, hazard: event.target.value })}
                    placeholder="Describe the hazard, exposure pathway and possible harm."
                  />
                </Field>
                <Field label="People affected" wide>
                  <input
                    value={draft.affectedPeople}
                    onChange={(event) => setDraft({ ...draft, affectedPeople: event.target.value })}
                    placeholder="Workers, users, maintainers, public or others"
                  />
                </Field>
                <Field label="Elimination or safer alternatives considered" wide>
                  <textarea
                    rows={3}
                    value={draft.eliminationConsidered}
                    onChange={(event) =>
                      setDraft({ ...draft, eliminationConsidered: event.target.value })
                    }
                  />
                </Field>
                <Field label="Selected design decision and rationale" wide>
                  <textarea
                    rows={3}
                    value={draft.designDecision}
                    onChange={(event) =>
                      setDraft({ ...draft, designDecision: event.target.value })
                    }
                  />
                </Field>
                <Field label="Residual control or required action" wide>
                  <textarea
                    rows={3}
                    value={draft.control}
                    onChange={(event) => setDraft({ ...draft, control: event.target.value })}
                  />
                </Field>
                <Field label="Drawing or specification reference">
                  <input
                    value={draft.drawingReference}
                    onChange={(event) =>
                      setDraft({ ...draft, drawingReference: event.target.value })
                    }
                    placeholder="Document ID and revision"
                  />
                </Field>
                <Field label="Design-risk owner">
                  <input
                    value={draft.owner}
                    onChange={(event) => setDraft({ ...draft, owner: event.target.value })}
                  />
                </Field>
                <Field label="Review status">
                  <select
                    value={draft.reviewStatus}
                    onChange={(event) =>
                      setDraft({ ...draft, reviewStatus: event.target.value as ReviewStatus })
                    }
                  >
                    <option value="draft">Draft</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="approved">Approved</option>
                  </select>
                </Field>
                <Field label="Named reviewer">
                  <input
                    value={draft.reviewer}
                    onChange={(event) => setDraft({ ...draft, reviewer: event.target.value })}
                  />
                </Field>
                <Field label="Review date">
                  <input
                    type="date"
                    value={draft.reviewDate}
                    onChange={(event) => setDraft({ ...draft, reviewDate: event.target.value })}
                  />
                </Field>
                <Field label="Action due date">
                  <input
                    type="date"
                    value={draft.dueDate}
                    onChange={(event) => setDraft({ ...draft, dueDate: event.target.value })}
                  />
                </Field>
                <Field label="Critical controls" wide>
                  <div className="check-grid">
                    {controls.map((control) => (
                      <label key={control.id}>
                        <input
                          type="checkbox"
                          checked={draft.controlIds.includes(control.id)}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              controlIds: event.target.checked
                                ? [...draft.controlIds, control.id]
                                : draft.controlIds.filter((id) => id !== control.id),
                            })
                          }
                        />
                        <span>{control.title || "Untitled control"}</span>
                      </label>
                    ))}
                  </div>
                </Field>
                <Field label="Controlled revisions" wide>
                  <div className="check-grid">
                    {revisions.map((revision) => (
                      <label key={revision.id}>
                        <input
                          type="checkbox"
                          checked={draft.revisionIds.includes(revision.id)}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              revisionIds: event.target.checked
                                ? [...draft.revisionIds, revision.id]
                                : draft.revisionIds.filter((id) => id !== revision.id),
                            })
                          }
                        />
                        <span>{revision.documentNumber || "No number"} · {revision.revision}</span>
                      </label>
                    ))}
                  </div>
                </Field>
                <Field label="Incident learning" wide>
                  <div className="check-grid">
                    {incidents.map((incident) => (
                      <label key={incident.id}>
                        <input
                          type="checkbox"
                          checked={draft.incidentIds.includes(incident.id)}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              incidentIds: event.target.checked
                                ? [...draft.incidentIds, incident.id]
                                : draft.incidentIds.filter((id) => id !== incident.id),
                            })
                          }
                        />
                        <span>{incident.title || "Untitled learning record"}</span>
                      </label>
                    ))}
                  </div>
                </Field>
                <Field label="Linked evidence" wide>
                  <div className="check-grid">
                    {evidence.length === 0 ? (
                      <p className="field-note">Add evidence records before linking them here.</p>
                    ) : (
                      evidence.map((item) => (
                        <label key={item.id}>
                          <input
                            type="checkbox"
                            checked={draft.evidenceIds.includes(item.id)}
                            onChange={(event) =>
                              setDraft({
                                ...draft,
                                evidenceIds: event.target.checked
                                  ? [...draft.evidenceIds, item.id]
                                  : draft.evidenceIds.filter((id) => id !== item.id),
                              })
                            }
                          />
                          <span>{item.title || "Untitled evidence"}</span>
                        </label>
                      ))
                    )}
                  </div>
                </Field>
              </div>
            </div>
            <div className="modal-actions">
              <div className="quality-summary">
                {riskCompletionIssues(draft).length ? (
                  <>
                    <CircleAlert size={17} />
                    {riskCompletionIssues(draft).length} quality gap
                    {riskCompletionIssues(draft).length === 1 ? "" : "s"} remain
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={17} /> Required fields complete
                  </>
                )}
              </div>
              <button className="button button-secondary" onClick={() => setDraft(null)}>
                Cancel
              </button>
              <button className="button button-primary" onClick={saveDraft}>
                Save design risk
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
